import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePublicSettings } from '../contexts/PublicSettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCmsContent } from '../hooks/useCmsContent';
import { LanguageSwitcher, LanguageSwitcherInline } from './LanguageSwitcher';

const DEFAULT_NAV = [
  { key: 'home', path: '/' },
  { key: 'courses', path: '/courses' },
  { key: 'pricing', path: '/pricing' },
  { key: 'about', path: '/about' },
  { key: 'contact', path: '/contact' },
];

export const Header = () => {
  const { settings } = usePublicSettings();
  const { t } = useLanguage();
  const cms = useCmsContent('header');

  const logoUrl      = cms.get('logo', 'logo_url', '/logo.png');
  const ctaPrimaryLink = cms.get('cta', 'primary_link',  '/level-test');
  const ctaPrimary   = cms.get('cta', 'primary_label', t('nav.levelTest'));
  const ctaSecondary = cms.get('cta', 'secondary_label', t('nav.bookTrial'));
  const ctaSecondaryLink = cms.get('cta', 'secondary_link', '/book-trial');
  const cmsNav       = cms.getList('nav', []);
  const navLinks     = (cmsNav.length > 0
    ? cmsNav.map(item => ({ name: item.label || item.name, path: item.path }))
    : DEFAULT_NAV.map(item => ({ name: t(`nav.${item.key}`), path: item.path }))
  ).filter(link => link.path !== '/level-test');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const siteName = settings.site_name || "Frankly Talking English";

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const getDashboardLink = () => {
    return isAdmin ? '/admin/dashboard' : '/student/dashboard';
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 bg-white border-t-[3px] border-t-[#D72B2B] transition-all duration-300 ease-out ${
        isScrolled 
          ? 'h-[68px] shadow-[0_4px_20px_rgba(0,0,0,0.10)]' 
          : 'h-20 border-b border-[#E8EAF0]'
      }`}
    >
      <div className="container-custom h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo with adequate margin */}
          <Link 
            to="/" 
            className="flex items-center mr-12 focus:outline-2 focus:outline-[#D72B2B] focus:outline-offset-2 rounded" 
            data-testid="logo-link"
          >
            <img 
              src={logoUrl}
              alt={siteName}
              className={`w-auto transition-all duration-300 ${isScrolled ? 'h-12' : 'h-14'}`}
              width="180"
              height="56"
            />
          </Link>

          {/* Desktop Navigation with animated underlines */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`relative text-sm font-medium transition-colors duration-200 py-2 group focus:outline-none ${
                  location.pathname === link.path
                    ? 'text-[#D72B2B]'
                    : 'text-[#1A2E6E] hover:text-[#D72B2B]'
                }`}
              >
                {link.name}
                {/* Animated underline */}
                <span 
                  className={`absolute bottom-0 left-0 h-[2px] bg-[#D72B2B] transition-transform duration-300 ease-out ${
                    location.pathname === link.path 
                      ? 'w-full scale-x-100' 
                      : 'w-full scale-x-0 group-hover:scale-x-100 origin-left'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA Buttons - refined with pill shape and scale effect */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher tone="light" />

            <Link
              to={ctaPrimaryLink}
              className="whitespace-nowrap text-sm font-semibold py-2.5 px-6 rounded-[50px] text-[#1A2E6E] border-2 border-[#1A2E6E]/20 hover:border-[#D72B2B] hover:text-[#D72B2B] hover:scale-[1.03] transition-all duration-200 focus:outline-2 focus:outline-[#D72B2B] focus:outline-offset-2"
              data-testid="header-level-test-btn"
            >
              {ctaPrimary}
            </Link>
            <Link
              to={ctaSecondaryLink}
              className="whitespace-nowrap text-sm font-semibold py-2.5 px-6 rounded-[50px] bg-[#D72B2B] text-white hover:bg-[#B71C1C] hover:scale-[1.03] shadow-md hover:shadow-lg transition-all duration-200 focus:outline-2 focus:outline-[#D72B2B] focus:outline-offset-2"
              data-testid="header-book-trial-btn"
            >
              {ctaSecondary}
            </Link>

            {/* Login/User Menu */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 py-2 px-4 rounded-[50px] bg-[#1A2E6E] text-white hover:bg-[#2A4A8E] transition-all duration-200"
                  data-testid="header-user-menu-btn"
                >
                  <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm font-medium max-w-[100px] truncate">
                    {user?.name?.split(' ')[0] || 'User'}
                  </span>
                </button>
                
                {/* User Dropdown */}
                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-[#E8EAF0] py-2 z-50"
                    >
                      <div className="px-4 py-2 border-b border-[#E8EAF0]">
                        <p className="text-sm font-medium text-[#1A2E6E] truncate">{user?.name}</p>
                        <p className="text-xs text-[#1A2E6E]/60 truncate">{user?.email}</p>
                      </div>
                      <Link
                        to={getDashboardLink()}
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors"
                      >
                        <LayoutDashboard size={16} />
                        {isAdmin ? t('header.adminPortal') : t('header.studentPortal')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={16} />
                        {t('header.signOut')}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="whitespace-nowrap flex items-center gap-2 text-sm font-semibold py-2.5 px-5 rounded-[50px] bg-[#1A2E6E] text-white hover:bg-[#2A4A8E] hover:scale-[1.03] transition-all duration-200"
                data-testid="header-login-btn"
              >
                <User size={16} />
                {t('header.login')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors focus:outline-2 focus:outline-[#D72B2B] focus:outline-offset-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={t('header.toggleMenu')}
            aria-expanded={isMobileMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-[rgba(15,25,75,0.97)] z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="lg:hidden fixed top-0 right-0 h-full w-[85%] max-w-[400px] bg-[rgba(15,25,75,0.98)] z-50 overflow-y-auto"
              data-testid="mobile-menu"
            >
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <img 
                  src={logoUrl}
                  alt={siteName}
                  className="h-12 w-auto brightness-0 invert"
                  width="150"
                  height="48"
                />
                <button
                  className="p-2 text-white hover:text-[#F5A623] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label={t('header.closeMenu')}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Mobile Nav Links */}
              <nav className="p-6">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                  >
                    <Link
                      to={link.path}
                      className={`block py-4 text-lg font-medium border-b border-white/10 transition-colors ${
                        location.pathname === link.path
                          ? 'text-[#F5A623]'
                          : 'text-white hover:text-[#F5A623]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                
                {/* Mobile CTA Buttons */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                  className="flex flex-col gap-3 mt-8"
                >
                  <Link
                    to={ctaPrimaryLink}
                    className="text-center py-3 px-6 rounded-[50px] text-white border-2 border-white/30 font-semibold hover:border-white hover:bg-white/10 transition-all"
                  >
                    {ctaPrimary}
                  </Link>
                  <Link
                    to={ctaSecondaryLink}
                    className="text-center py-3 px-6 rounded-[50px] bg-[#D72B2B] text-white font-semibold hover:bg-[#B71C1C] transition-all"
                  >
                    {ctaSecondary}
                  </Link>
                  
                  {/* Mobile Login/Portal */}
                  {isAuthenticated ? (
                    <>
                      <Link 
                        to={getDashboardLink()}
                        className="text-center py-3 px-6 rounded-[50px] bg-[#4CAF50] text-white font-semibold hover:bg-[#388E3C] transition-all flex items-center justify-center gap-2"
                      >
                        <LayoutDashboard size={18} />
                        {isAdmin ? t('header.adminPortal') : t('header.myPortal')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="text-center py-3 px-6 rounded-[50px] text-white/80 border border-white/20 font-semibold hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                      >
                        <LogOut size={18} />
                        {t('header.signOut')}
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      className="text-center py-3 px-6 rounded-[50px] bg-[#1A2E6E] text-white font-semibold hover:bg-[#2A4A8E] transition-all flex items-center justify-center gap-2"
                    >
                      <User size={18} />
                      {t('header.login')}
                    </Link>
                  )}
                </motion.div>

                <LanguageSwitcherInline />
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};
